import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiOutlineArrowUpRight, HiOutlineArrowRight } from "react-icons/hi2";
import RobotImg from "../../Assets/RobotImg2.png";
import sideLogo from "../../Assets/joinNow.png";

const Services = () => {
  const [activePage, setActivePage] = React.useState(1);

  // Variants for overall section stagger
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 35 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  // Letter variants for dramatic text reveal
  const letterVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", damping: 12, stiffness: 180 },
    },
  };

  // Word variants for paragraph
  const wordVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.07, duration: 0.5 },
    }),
  };

  // Helpers to split text
  const splitToLetters = (text) =>
    text.split("").map((char, idx) => (
      <motion.span
        key={idx}
        variants={letterVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="inline-block"
        style={{ whiteSpace: char === " " ? "pre" : "normal" }}
      >
        {char}
      </motion.span>
    ));

  const splitToWords = (text) =>
    text.split(" ").map((word, idx) => (
      <motion.span
        key={idx}
        custom={idx}
        variants={wordVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="inline-block mr-[0.3em]"
      >
        {word}
      </motion.span>
    ));

  const headingText = "Supply Chains with AI-Driven Insights";
  const paragraphText =
    "Lorem Ipsum is simply dummy text of the printing and typesetting industry.";

  return (
    <motion.section
      id="services"
      className="w-full bg-white overflow-hidden px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={containerVariants}
    >
      {/* GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 items-center">
        {/* LEFT CONTENT */}
        <motion.div
          className="flex flex-col items-start"
          variants={itemVariants}
        >
          <motion.div
            className="flex items-center gap-2 mb-3"
            variants={itemVariants}
          >
            <img src={sideLogo} alt="" className="w-7 sm:w-8" />
            <motion.span
              className="text-xs font-bold tracking-widest text-brandDark uppercase"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ staggerChildren: 0.07 }}
            >
              {"Services".split("").map((char, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                  className="inline-block"
                >
                  {char}
                </motion.span>
              ))}
            </motion.span>
          </motion.div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold leading-tight mb-6 text-brandDark">
            {splitToLetters(headingText)}
          </h2>

          <p className="text-brandDark text-sm sm:text-base leading-relaxed mb-8 opacity-70">
            {splitToWords(paragraphText)}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
            <motion.button
              className="bg-brandOrange text-white py-3 px-6 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-[#d44206] transition relative overflow-hidden group"
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <span className="relative z-10">Request Callback</span>
              <HiOutlineArrowUpRight className="text-lg relative z-10 group-hover:rotate-45 transition-transform" />

              <motion.div
                className="absolute inset-0 bg-white/20"
                initial={{ x: "-100%" }}
                whileHover={{ x: "100%" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              />
            </motion.button>

            <motion.button
              className="border border-brandOrange text-brandOrange py-3 px-6 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-orange-50 transition relative overflow-hidden group"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              <span className="relative z-10">CTA Button</span>
              <HiOutlineArrowUpRight className="text-lg relative z-10 group-hover:rotate-45 transition-transform" />
            </motion.button>
          </div>
        </motion.div>

        {/* CENTER IMAGE */}
        <motion.div
          className="w-full"
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, delay: 0.3, type: "spring" }}
        >
          <div className="rounded-xl overflow-hidden shadow-lg">
            <motion.img
              src={RobotImg}
              alt="AI Warehouse Operations"
              className="w-full h-full object-cover aspect-[4/3]"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </motion.div>

        {/* RIGHT CARDS */}
        <motion.div
          className="w-full lg:col-span-2 xl:col-span-1"
          variants={itemVariants}
        >
          <div className="flex flex-col md:flex-row xl:flex-col gap-6">
            {[1, 2].map((i) => (
              <motion.div
                key={i}
                className="bg-[#F4FAFF] p-5 sm:p-6 md:p-8 rounded-xl border border-blue-50 cursor-pointer transition hover:shadow-lg hover:-translate-y-1"
                whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2, duration: 0.7 }}
              >
                <p className="text-brandDark text-sm sm:text-base leading-relaxed font-medium mb-4">
                  Lorem Ipsum has been the industry's standard dummy text ever
                  since the 1500s.
                </p>
                <HiOutlineArrowRight className="text-brandCyan text-xl" />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* PAGINATION – with scale & color animation */}
      <div className="flex justify-center gap-3 sm:gap-4 mt-4">
        {[1, 2, 3, 4].map((num) => (
          <motion.button
            key={num}
            onClick={() => setActivePage(num)}
            className={`text-base sm:text-lg font-bold transition ${
              activePage === num
                ? "text-brandCyan scale-125"
                : "text-gray-400 hover:text-brandDark"
            }`}
            whileHover={{ scale: activePage === num ? 1.35 : 1.15 }}
            whileTap={{ scale: 0.9 }}
            animate={{
              scale: activePage === num ? 1.25 : 1,
              color: activePage === num ? "#06b6d4" : "#9ca3af", // assuming brandCyan is cyan-ish
            }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            {num}
          </motion.button>
        ))}
      </div>
    </motion.section>
  );
};

export default Services;
